import React, { useContext } from 'react';
import { PapiGallection } from '@wix/da-papi-types';
import classnames from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { DraggableType } from '../../../types/dnd';
import Grid from '../../../Grid';
import {
  DndSortable,
  DndSortableChildrenProps,
  DndSortableElement,
} from '../../../Dnd/DndSortable';
import { MoveGallectionToPositionPayload } from '../../actions';
import { isSpecialStuckGallection } from '../../helpers';
import { FOLDER_CARD_ASPECT_RATIO } from '../../../FolderCard/FolderCard';
import { useGlobalPrivs } from '../../_hocs/withGlobalPrivs';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';

import s from './GallectionGrid.scss';

export interface Props {
  gallections: PapiGallection[];
  gallectionRenderer: ({ index, width }) => React.ReactNode;
  cookieType: MeasuredCookieType;
  moveGallectionToPosition?: (payload: MoveGallectionToPositionPayload) => void;
}

const GallectionGrid: React.FC<Props> = ({
  gallections,
  cookieType,
  gallectionRenderer,
  moveGallectionToPosition,
}) => {
  const isMobile = useContext(MobileContext);
  const { canRearrangeGalleryFolders } = useGlobalPrivs();

  if (!gallections) {
    return null;
  }

  const elementAspectRatio = FOLDER_CARD_ASPECT_RATIO;

  const getFolderId = (index: number) => {
    const item = gallections[index];
    return item.folderId;
  };

  const renderDndGallection =
    ({ draggedElementId, ...dndProps }: DndSortableChildrenProps) =>
    options => {
      const { style, index } = options;
      const item = gallections[index];

      const canMoveThisGallection =
        !isSpecialStuckGallection(item) &&
        canRearrangeGalleryFolders &&
        !!moveGallectionToPosition;

      if (!canMoveThisGallection) {
        return <div style={style}>{gallectionRenderer(options)}</div>;
      }
      const isDragging = draggedElementId === item.folderId;
      return (
        <DndSortableElement
          dndType={DraggableType.MEDIA_MANAGER_GALLECTION}
          id={item.folderId}
          key={item.folderId}
          position={index}
          isSortable={canMoveThisGallection}
          {...dndProps}
        >
          {sortableOptions => (
            <div
              id={`${item.folderId}`}
              ref={sortableOptions.ref}
              key={item.folderId}
              style={{
                marginLeft: style.marginLeft,
              }}
              className={classnames(isDragging && s['dragging'])}
            >
              {gallectionRenderer(options)}
            </div>
          )}
        </DndSortableElement>
      );
    };

  const onDndDragOver = (id: number, position: number) => {
    moveGallectionToPosition!({
      folderId: id,
      position,
      moveType: 'streamOnly',
    });
  };

  const onDndDrop = (id: number, position: number) => {
    moveGallectionToPosition!({
      folderId: id,
      position,
      moveType: 'papiOnly',
    });
  };

  return (
    <DndSortable onDragOver={onDndDragOver} onDrop={onDndDrop}>
      {props => {
        const renderElement = renderDndGallection(props);
        return (
          <Grid
            className={s['root']}
            breakByScreenWidth={false}
            breakpoints={[
              { maxWidth: 400, elementsPerRow: 1, elementAspectRatio },
              { maxWidth: 550, elementsPerRow: 2, elementAspectRatio },
              { maxWidth: 800, elementsPerRow: 3, elementAspectRatio },
              { maxWidth: 9999, elementsPerRow: 4, elementAspectRatio },
            ]}
            cookieType={cookieType}
            elementCount={gallections.length}
            getElementId={getFolderId}
            renderElement={renderElement}
            spacing={isMobile ? 10 : 16}
            enableScrollOptim={false}
          />
        );
      }}
    </DndSortable>
  );
};
GallectionGrid.displayName = 'GallectionGrid';

export default GallectionGrid;
